/** @module Short feedback from any page: kind, thumb, a few words; page and device go along. */
import { useMutation } from "@tanstack/react-query";
import { MessageSquarePlus, Send, ThumbsDown, ThumbsUp } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { api, problemFrom } from "@/api/client";
import { Sheet, useToast } from "@/components/ui/overlays";
import { APP_VERSION } from "@/lib/app-version";

export const FEEDBACK_KINDS = ["bug", "idea", "praise", "other"] as const;
export type FeedbackKind = (typeof FEEDBACK_KINDS)[number];
type Rating = -1 | 1;

export interface FeedbackDraft {
  kind?: FeedbackKind;
  rating?: Rating | null;
  /** The scan run the feedback is about, when it was asked for after one. */
  scanSessionId?: string | null;
}

function Form({ draft, onSent }: { draft: FeedbackDraft; onSent: () => void }) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<FeedbackKind>(draft.kind ?? "other");
  const [rating, setRating] = useState<Rating | null>(draft.rating ?? null);
  const [message, setMessage] = useState("");
  const send = useMutation({
    mutationFn: async () => {
      const { data, response } = await api.POST("/api/v1/feedback", {
        body: {
          kind,
          rating,
          message,
          context: {
            // The address, not the router: the sheet must work outside a route as well.
            route: window.location.pathname,
            app_version: APP_VERSION,
            viewport: `${window.innerWidth}x${window.innerHeight}`,
            scan_session_id: draft.scanSessionId ?? null,
          },
        },
      });
      if (!data) throw problemFrom(response);
      return data;
    },
    onSuccess: onSent,
  });
  const empty = message.trim() === "" && rating === null;

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (!empty) send.mutate();
      }}
    >
      <h2 className="t-h3">{t("feedback.title")}</h2>
      <div className="flex flex-wrap gap-2" role="group" aria-label={t("feedback.kind")}>
        {FEEDBACK_KINDS.map((option) => (
          <button
            key={option}
            type="button"
            className="chip"
            aria-pressed={kind === option}
            onClick={() => {
              setKind(option);
            }}
          >
            {t(`feedback.kinds.${option}`)}
          </button>
        ))}
      </div>
      <div className="row gap-2" role="group" aria-label={t("feedback.rating")}>
        <button
          type="button"
          className="chip"
          aria-pressed={rating === 1}
          aria-label={t("feedback.good")}
          onClick={() => {
            setRating((current) => (current === 1 ? null : 1));
          }}
        >
          <ThumbsUp />
        </button>
        <button
          type="button"
          className="chip"
          aria-pressed={rating === -1}
          aria-label={t("feedback.bad")}
          onClick={() => {
            setRating((current) => (current === -1 ? null : -1));
          }}
        >
          <ThumbsDown />
        </button>
      </div>
      <label className="field">
        <span className="field-label">{t("feedback.message")}</span>
        <textarea
          className="input min-h-28 py-2"
          maxLength={2000}
          value={message}
          placeholder={t("feedback.placeholder")}
          onChange={(event) => {
            setMessage(event.target.value);
          }}
        />
      </label>
      <p className="t-xs t-muted">{t("feedback.context")}</p>
      {send.isError ? (
        <p role="alert" className="t-sm loss">
          {t("feedback.error")}
        </p>
      ) : null}
      <button
        type="submit"
        className="btn btn-primary btn-block"
        disabled={empty || send.isPending}
      >
        <Send />
        {t("feedback.send")}
      </button>
    </form>
  );
}

/** The sheet itself; callers decide when it opens and what it starts with. */
export function FeedbackSheet({
  open,
  onClose,
  draft = {},
}: {
  open: boolean;
  onClose: () => void;
  draft?: FeedbackDraft;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  return (
    <Sheet open={open} onClose={onClose} label={t("feedback.title")}>
      <Form
        draft={draft}
        onSent={() => {
          toast.show({ text: t("feedback.thanks") });
          onClose();
        }}
      />
    </Sheet>
  );
}

/** The header button: feedback is one tap away on every page. */
export function FeedbackButton() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className="icon-btn"
        aria-label={t("feedback.open")}
        title={t("feedback.open")}
        onClick={() => {
          setOpen(true);
        }}
      >
        <MessageSquarePlus />
      </button>
      <FeedbackSheet
        open={open}
        onClose={() => {
          setOpen(false);
        }}
      />
    </>
  );
}
